import React from 'react';
import { X } from 'lucide-react';
import { getMusicTrackLabel } from '../utils/musicTrackNames';

export const fieldLabelClass = 'block text-xs font-semibold text-gray-600 mb-1.5';
export const fieldInputClass =
  'w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500';

export const FieldError = ({ message }) => {
  if (!message) return null;
  return <p className="mt-1 text-xs text-red-600">{message}</p>;
};

// Logo and music blocks are shared by the Basic and Advanced modes of the editor form.
export const LogoField = ({ config, fieldErrors, submitting, logoInputRef, onInputChange, onLogoChange, onClearLogo }) => (
  <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-3">
    <label className={fieldLabelClass}>Logo (opcional)</label>
    <div className="flex items-center gap-3">
      <input
        ref={logoInputRef}
        type="file"
        accept="image/*"
        onChange={onLogoChange}
        disabled={submitting}
        className="flex-1 text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
      />
      {config.logo && (
        <button
          type="button"
          onClick={onClearLogo}
          disabled={submitting}
          className="flex-shrink-0 p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
          aria-label="Quitar logo"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
    <FieldError message={fieldErrors.logo} />

    <label className="flex items-center gap-2 text-sm text-gray-700">
      <input
        type="checkbox"
        name="useClientLogo"
        checked={config.useClientLogo}
        onChange={onInputChange}
        disabled={submitting || Boolean(config.logo)}
        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
      />
      Usar el logo del cliente
    </label>
    {config.logo && (
      <p className="text-xs text-gray-500">
        Se subió un archivo de logo, por lo que "Usar el logo del cliente" queda deshabilitado (el archivo tiene prioridad).
      </p>
    )}
    {!config.logo && !config.useClientLogo && (
      <p className="text-xs text-gray-500">
        Si no se elige ningún logo, el video se genera sin logo.
      </p>
    )}
    <FieldError message={fieldErrors.useClientLogo} />
  </div>
);

export const MusicField = ({ config, fieldErrors, submitting, musicTracks, musicTracksError, onInputChange }) => {
  const selectedTrackUrl = musicTracks.find((track) => track.fileName === config.musicTrack)?.url;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
      <div>
        <label className={fieldLabelClass}>Música (opcional)</label>
        <select
          name="musicTrack"
          value={config.musicTrack}
          onChange={onInputChange}
          className={fieldInputClass}
          disabled={submitting}
        >
          <option value="">Sin música</option>
          {musicTracks.map((track) => (
            <option key={track.key} value={track.fileName}>
              {getMusicTrackLabel(track.fileName)}
            </option>
          ))}
        </select>
        {musicTracksError && <p className="mt-1 text-xs text-amber-600">{musicTracksError}</p>}
        <FieldError message={fieldErrors.musicTrack} />
        {selectedTrackUrl && (
          <audio key={selectedTrackUrl} controls src={selectedTrackUrl} className="w-full mt-2 h-9" />
        )}
      </div>
      <div>
        <label className={fieldLabelClass}>Volumen de música (0-100, opcional)</label>
        <input
          type="number"
          name="musicVolume"
          min="0"
          max="100"
          value={config.musicVolume}
          onChange={onInputChange}
          placeholder="100"
          className={fieldInputClass}
          disabled={submitting}
        />
        <FieldError message={fieldErrors.musicVolume} />
        <p className="mt-1 text-xs text-gray-500">
          Requiere elegir una música. Si se deja vacío, el volumen queda predefinido.
        </p>
      </div>
    </div>
  );
};

// Mirrors the backend cap on WhatsApp recipients per schedule.
export const MAX_RECIPIENTS = 10;

export const RecipientsField = ({ recipients, selectedIds, loading, error, submitting, fieldError, onToggle }) => {
  const limitReached = selectedIds.length >= MAX_RECIPIENTS;
  return (
    <div className="sm:col-span-2">
      <label className={fieldLabelClass}>Enviar por WhatsApp a (opcional)</label>
      {loading && <p className="text-xs text-gray-500">Cargando usuarios...</p>}
      {!loading && error && <p className="text-xs text-red-600">{error}</p>}
      {!loading && !error && recipients.length === 0 && (
        <p className="text-xs text-gray-500">
          No hay usuarios activos con teléfono cargado. Agregalos desde Usuarios para recibir los videos por WhatsApp.
        </p>
      )}
      {!loading && recipients.length > 0 && (
        <div className="space-y-1.5">
          {recipients.map((u) => {
            const selected = selectedIds.includes(u.id);
            const fullName = [u.name, u.surname].filter(Boolean).join(' ');
            return (
              <label key={u.id} className="flex items-center gap-2 text-sm text-gray-800">
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => onToggle(u.id)}
                  disabled={submitting || (!selected && limitReached)}
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span>{fullName} <span className="text-xs text-gray-500">({u.phoneNumber})</span></span>
              </label>
            );
          })}
          <p className="text-xs text-gray-500">
            Cada video programado se envía a los seleccionados (máx. {MAX_RECIPIENTS}).
          </p>
        </div>
      )}
      <FieldError message={fieldError} />
    </div>
  );
};
